import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import DOMPurify from "dompurify";
import api from "@/lib/api";

export default function ArticleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/articles/${id}`);
        if (data.external_url) {
          window.location.replace(data.external_url);
          return;
        }
        setArticle(data);
      } catch {
        setNotFound(true);
      }
    })();
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6" data-testid="article-not-found">
        <div className="label-eyebrow">404</div>
        <h1 className="font-heading font-black text-6xl mt-4">Not found.</h1>
        <p className="mt-4 text-black/60">That article doesn't exist (or was unpublished).</p>
        <Link to="/" className="btn-primary mt-8">← Back home</Link>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center font-mono text-xs uppercase tracking-[0.3em] text-black/60">
        Loading…
      </div>
    );
  }

  const d = new Date(article.created_at);
  const dateStr = d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  return (
    <div className="min-h-screen bg-white text-black" data-testid="article-detail">
      <header className="border-b border-black sticky top-0 bg-white z-50">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="font-heading font-black text-lg tracking-tight">
            DOM<span className="text-[#FF4500]">.</span>LAFRANCE
          </Link>
          <button
            onClick={() => navigate(-1)}
            className="font-mono text-[11px] uppercase tracking-[0.18em] hover:text-[#FF4500] flex items-center gap-2"
            data-testid="article-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>
      </header>

      <article className="max-w-[860px] mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="label-eyebrow">{article.category} · {dateStr}</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mt-6">
          {article.title}
        </h1>
        <p className="mt-8 text-xl text-black/70 leading-relaxed">{article.excerpt}</p>

        {article.image_url && (
          <div className="mt-12 border border-black overflow-hidden">
            <img src={article.image_url} alt="" className="w-full h-auto" />
          </div>
        )}

        {(() => {
          const body = article.body || "";
          const isHtml = /<[a-z][\s\S]*>/i.test(body);
          if (isHtml) {
            const clean = DOMPurify.sanitize(body, { ADD_ATTR: ["target", "rel"] });
            return (
              <div
                className="article-body mt-12 max-w-none text-black/85 leading-relaxed"
                data-testid="article-body"
                dangerouslySetInnerHTML={{ __html: clean }}
              />
            );
          }
          return (
            <div
              className="article-body mt-12 max-w-none text-black/85 leading-relaxed whitespace-pre-line"
              data-testid="article-body"
            >
              {body}
            </div>
          );
        })()}

        <div className="mt-20 border-t border-black pt-10 flex items-center justify-between gap-4 flex-wrap">
          <Link to="/" className="btn-secondary inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back home
          </Link>
          <Link to="/#connect" className="btn-primary inline-flex items-center gap-2">
            Book a chat <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </article>
    </div>
  );
}
